<template>
  <Menu.Root id="header-overflow">
    <Menu.Trigger>
      <Tooltip.Hint :text="label">
        <button
          type="button"
          :aria-label="label"
          :data-emphasis-identifier="id"
          class="shrink-0 rounded-full border border-transparent p-[0.4375rem] text-gray-700 outline-2 outline-blue-500 transition hover:bg-gray-200 focus-visible:outline aria-expanded:bg-gray-200 sm:px-1.5 sm:py-1 dark:text-muted-foreground dark:hover:bg-card dark:aria-expanded:bg-card"
        >
          <EllipsisIcon class="size-4 sm:h-5" />
        </button>
      </Tooltip.Hint>
    </Menu.Trigger>

    <Menu.Container>
      <template v-for="(action, idx) in filtered" :key="idx">
        <Menu.RouteItem
          v-if="action.to"
          :icon="action.icon"
          :label="action.label"
          :disabled="action.disabled"
          :to="action.to"
        />
        <Menu.LinkItem
          v-else-if="action.href"
          :icon="action.icon"
          :label="action.label"
          :disabled="action.disabled"
          :href="action.href"
        />
        <Menu.Item
          v-else
          :icon="action.icon"
          :label="action.label"
          :variant="action.variant"
          :disabled="action.disabled"
          :hotkey="action.hotkey"
          @click="action.onClick"
        />
      </template>
    </Menu.Container>
  </Menu.Root>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import Menu from "@/design_system/Menu";
import type { Variant } from "@/design_system/Menu/Item.vue";
import Tooltip from "@/design_system/Tooltip";
import EllipsisIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";
import type { MappedIconMicro } from "@/icons/icon-mapping-micro";
import { type KeybindMapping, useKeybinds } from "@/lib/hotkeys";
import type { RouteLocation } from "@/router/types";

interface ActionItem {
  label: string;
  description?: string;
  hotkey?: string;
  icon: MappedIconMicro;
  variant?: Variant;
  disabled?: boolean;
  href?: string;
  to?: RouteLocation;
  onClick?: () => void;
}

type FalsyValue = 0 | false | undefined | null;
export type MaybeActionItem = ActionItem | FalsyValue;

const router = useRouter();

const props = withDefaults(
  defineProps<{
    label?: string;
    id?: string;
    actions: MaybeActionItem[];
  }>(),
  {
    label: `Actions`,
  }
);

const filtered = computed(() => {
  return props.actions.filter((v) => !!v);
});

useKeybinds(() => {
  return filtered.value.reduce((map: KeybindMapping, action) => {
    if (action.disabled || !action.hotkey) {
      return map;
    }

    map[action.hotkey] = {
      name: action.description ?? action.label,
      group: `Page`,
      run(ev) {
        if (ev.defaultPrevented) {
          return;
        }

        if (action.to) {
          router.push(action.to);
        } else if (action.onClick) {
          action.onClick();
        }

        return true;
      },
    };

    return map;
  }, {});
});
</script>
